import { WhitelabelConfigs } from "@/types/WhitelabelConfigs";
import { clsx, type ClassValue } from "clsx";
import { parseISO, format, subDays } from "date-fns";
import { generatePath } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { Paginator } from "../components/GeneralPagination";
import { customStorage } from "@/hooks/CustomLocalStorage";

/**
 * The only hostname that may show ProData's own copyright, Terms & Conditions and Privacy links.
 * Every whitelabel domain hides them. Override per environment with PDMG_PROD_PORTAL_HOSTNAME.
 */
const PRODATA_PORTAL_HOSTNAME = "agencyportal.prodata.media";

export type AppEnvs = ImportMetaEnv & {
	PDMG_API_URL: string;
	PDMG_API_HOST: string;
	PDMG_PROD_PORTAL_HOSTNAME: string;
};

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

export function getEnvs(): {
	PDMG_API_URL: string;
	PDMG_API_HOST: string;
	PDMG_PROD_PORTAL_HOSTNAME: string;
	DEPLOY_API: string;
} {
	const envs = Object.freeze(import.meta.env) as AppEnvs;
	return {
		PDMG_API_URL: envs?.PDMG_API_URL ?? "",
		PDMG_API_HOST: envs?.PDMG_API_HOST ?? "",
		PDMG_PROD_PORTAL_HOSTNAME: envs?.PDMG_PROD_PORTAL_HOSTNAME || PRODATA_PORTAL_HOSTNAME,
		DEPLOY_API: envs?.DEPLOY_API ?? "http://report-site.com/take5/newcampaign2",
	};
}

export function formatDate(date: Date | string, pattern = "MM/dd/yyyy"): string {
	if (typeof date === "string") {
		date = parseISO(date);
	}
	return format(date, pattern);
}

export function setPageTitle(companyName: string) {
	const title = window.document.querySelector("title");
	if (title) {
		title.innerHTML = `${companyName} - Agency Portal`;
	}
}

export function setDynamicStyles(whitelabel: WhitelabelConfigs) {
	const dynamicStyles = window.document.querySelector<HTMLStyleElement>("#dynamic-styles");
	if (dynamicStyles) {
		dynamicStyles.innerText = `:root {--whitelabel-signup-btn: ${whitelabel.whitelabel_signup_button}; }`;
	}
}

export function downloadResource(blob: Blob, fileName: string) {
	const url = window.URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = fileName;
	document.body.appendChild(a);
	a.click();

	// Cleanup
	window.URL.revokeObjectURL(url);
	document.body.removeChild(a);
}

export function getParseOrderSearchDefaultData(orderSearchDefault: string): {
	path: string;
	url: string;
	params: {
		startDate: string;
		endDate: string;
		status: string;
	};
} {
	const defaultParams = {
		startDate: format(subDays(new Date(), 63), "yyyy-MM-dd"),
		endDate: format(new Date(), "yyyy-MM-dd"),
		status: "GLOBAL",
	};

	if (!orderSearchDefault || typeof orderSearchDefault !== "string" || orderSearchDefault.trim() === "") {
		const path = ":status?/:startDate?/:endDate";
		return {
			path,
			url: generateAgencyOrderListingPageUrl(path, defaultParams),
			params: defaultParams,
		};
	}

	const segments = orderSearchDefault.split("/");
	const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
	if (segments.length === 1 && dateRegex.test(segments[0])) {
		const path = ":status?/:startDate?/:endDate";
		const params = {
			...defaultParams,
			startDate: segments[0],
		};
		return {
			path,
			url: generateAgencyOrderListingPageUrl(path, params),
			params,
		};
	}

	const pathResult = [];
	let hasStartDate = false;
	let hasStatus = false;
	let hasEndDate = false;

	// First pass: Build the result and extract values based on the input order
	segments.forEach(segment => {
		if (dateRegex.test(segment)) {
			if (!hasStartDate) {
				pathResult.push(":startDate?");
				defaultParams.startDate = segment;
				hasStartDate = true;
			} else if (!hasEndDate) {
				pathResult.push(":endDate?");
				defaultParams.endDate = segment;
				hasEndDate = true;
			}
		} else {
			if (!hasStatus) {
				pathResult.push(":status?");
				defaultParams.status = segment;
				hasStatus = true;
			}
		}
	});

	if (!hasStartDate) {
		pathResult.push(":startDate?");
	}
	if (!hasStatus) {
		pathResult.push(":status?");
	}
	if (!hasEndDate) {
		pathResult.push(":endDate?");
	}

	const path = `${pathResult.join("/")}/:agency?/:perPage?/:page?/:sortColumn?/:sortDir?`;
	return {
		path,
		url: generateAgencyOrderListingPageUrl(path, defaultParams),
		params: defaultParams,
	};
}

export function generateAgencyOrderListingPageUrl(
	path: string,
	{
		startDate = "",
		endDate = "",
		status = "",
		agencyFilter = "-",
		perPage = Paginator.defaultPageSize as number,
		page = 1,
		sortColumn = "o.create_time",
		sortDir = "desc",
	}: {
		startDate?: string;
		endDate?: string;
		status?: string;
		agencyFilter?: string | string[];
		perPage?: number;
		page?: number;
		sortColumn?: string;
		sortDir?: string;
	},
) {
	const urlPath = generatePath(path, {
		startDate,
		endDate,
		status,
		agency: Array.isArray(agencyFilter) ? agencyFilter.join(",") : agencyFilter,
		perPage: String(perPage),
		page: String(page),
		sortColumn,
		sortDir,
	});
	return `/agency-order/order-listing/${urlPath}`;
}

export function getPrefixBeforeSlash(input: string): string {
	return input.includes("/") ? input.split("/")[0] : input;
}

export const parseOnOff = (value: string | undefined): boolean | null => {
	const normalized = (value ?? "").trim().toLowerCase();
	return (
		normalized === "on" ? true
		: normalized === "off" ? false
		: null
	);
};

export function getAgencyIdByStoredPassKeyEmail(email: string) {
	const raw = customStorage.getItem("passkeyEmails") as string | null;
	if (!raw) return null;

	let storedEmails: Record<string, string[]>;
	try {
		storedEmails = JSON.parse(raw) as Record<string, string[]>;
	} catch {
		return null;
	}

	return Object.keys(storedEmails).find(key => storedEmails[key].includes(email)) ?? null;
}

export const getStatusColor = (status: string) => {
	switch (status.toLowerCase()) {
		case "pending":
			return "bg-yellow-100 text-yellow-800";
		case "processing":
			return "bg-blue-100 text-blue-800";
		case "sending":
			return "bg-orange-100 text-orange-800";
		case "sent":
			return "bg-green-100 text-green-800";
		case "failed":
			return "bg-red-100 text-red-800";
		default:
			return "bg-gray-100 text-gray-800";
	}
};

export function geoDataToArray(geoData: string | string[], glue: string | null = null): string[] {
	if (!geoData || (typeof geoData !== "string" && !Array.isArray(geoData))) {
		return [];
	}

	if (Array.isArray(geoData)) {
		return geoData;
	}

	const data = geoData.trim();
	glue ??= data.includes(",") ? "," : " ";
	return data
		.split(glue)
		.filter(Boolean)
		.map(val => String(val).trim());
}

export function isProdataPortal(): boolean {
	return window.location.hostname === getEnvs().PDMG_PROD_PORTAL_HOSTNAME;
}
